import { CreateClaimProviderType, SupportProvider } from '../../../../api';
import { FormikProps } from 'formik';
import React, { useCallback } from 'react';
import * as S from './additional-providers-form.styles';
import { AsyncSelect, ISelectOption } from '../../../base';
import { FormField } from '../../../form-field';
import { ClaimEditorStore } from '../../../../stores/claim-editor';

interface ProviderAutocompleteProps {
    providerType: CreateClaimProviderType;
    formik: FormikProps<any>;
    onSelect: (provider: SupportProvider) => void;
    initialProvider?: SupportProvider;
    claimEditorStore: ClaimEditorStore;
}

export const ProviderAutocomplete: React.FunctionComponent<ProviderAutocompleteProps> = ({
    providerType,
    formik,
    onSelect,
    initialProvider,
    claimEditorStore,
}) => {
    // Store mapping of provider IDs to full provider objects
    const providersMapRef = React.useRef<Map<string, SupportProvider>>(new Map());

    const getProviderDisplayName = (provider: SupportProvider): string => {
        if (provider.first || provider.last) {
            return [provider.first, provider.last].filter(Boolean).join(' ') + ' | ' + (provider.npi ? `NPI: ${provider.npi}` : '');
        }
        const displayName = provider.organization ? provider.organization + ' | ' + (provider.npi ? `NPI: ${provider.npi}` : '') : '';
        return displayName || provider.id || '';
    };

    const loadProviderOptions = useCallback(
        async (inputValue: string): Promise<ISelectOption[]> => {
            const query = (inputValue || '').trim().toLowerCase();
            try {
                // Use preloaded providers; ClaimEditor is responsible for preloading once
                const all: SupportProvider[] = claimEditorStore.allProviders;

                const startsWith = (val?: string | null) => (val ? val.toLowerCase().startsWith(query) : false);
                const normalize = (val?: string | null) => (val ? val.toLowerCase() : '');

                const filteredByType = all.filter(provider => {
                    if (providerType) {
                        return String((provider as any).type).toUpperCase() === String(providerType).toUpperCase();
                    }
                    return true;
                });

                const filtered = filteredByType.filter(provider => {
                    // If no query, include all providers of the requested type
                    if (!query) {
                        return true;
                    }

                    const first = normalize((provider as any).first);
                    const last = normalize((provider as any).last);
                    const organization = normalize((provider as any).organization);
                    const npi = normalize((provider as any).npi);

                    const firstLast = [first, last].filter(Boolean).join(' ');
                    const lastFirst = [last, first].filter(Boolean).join(' ');

                    return (
                        startsWith(organization) ||
                        startsWith(first) ||
                        startsWith(last) ||
                        startsWith(firstLast) ||
                        startsWith(lastFirst) ||
                        startsWith(npi)
                    );
                });

                // Store providers in map for later retrieval
                filtered.forEach(provider => {
                    if (provider.id) {
                        providersMapRef.current.set(provider.id, provider);
                    }
                });

                return filtered.map(provider => ({
                    label: getProviderDisplayName(provider),
                    value: provider.id || '',
                }));
            } catch (error) {
                console.error('Failed to search providers:', error);
                return [];
            }
        },
        [claimEditorStore, providerType],
    );

    const handleProviderChange = (option: ISelectOption | null) => {
        if (option && option.value) {
            const provider = providersMapRef.current.get(option.value as string);
            if (provider) {
                onSelect(provider);
            }
        }
    };

    return (
        <S.FormField span={3}>
            <FormField label={`${providerType.charAt(0)}${providerType.slice(1).toLowerCase()} Provider Lookup`}>
                <AsyncSelect
                    loadOptions={loadProviderOptions}
                    onChange={(opt: any) => handleProviderChange(opt as ISelectOption | null)}
                    defaultOptions
                    placeholder="Search by organization, name, or NPI..."
                    isClearable
                />
            </FormField>
        </S.FormField>
    );
};

